import React, { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { X, UploadSimple, MagnifyingGlass, Check, Image as ImageIcon } from "phosphor-react"
import { toast } from "sonner"
import axiosInstance from "../../utils/axios/axiosConfig"

const ease = [0.16, 1, 0.3, 1]

function fileExt(url = "") {
  const clean = String(url || "").split("?")[0]
  const parts = clean.split(".")
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ""
}

function fileToBase64DataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file provided"))

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== "string") return reject(new Error("Invalid base64 result"))
      if (!result.startsWith("data:image/")) return reject(new Error("Not an image base64 data URL"))
      resolve(result)
    }
    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsDataURL(file)
  })
}

// ✅ API helpers (keep here to avoid mismatched signatures)
async function apiListMedia({ folder, page, q }) {
  // If your backend doesn't support q yet, it will just ignore it (safe)
  return axiosInstance.get("/admin/media", { params: { folder, page, q } })
}

async function apiUploadMedia({ image, folder, name }) {
  // Laravel expects JSON with keys: image, folder, name
  return axiosInstance.post("/admin/media/upload", { image, folder, name })
}

export default function MediaLibraryModal({
  open,
  onClose,
  onPick, // (asset) or (assets[])
  folder = "content",
  multiple = false,
  accept = "image/*",
}) {
  const fileRef = useRef(null)

  const [tab, setTab] = useState("library") // library | upload
  const [loading, setLoading] = useState(false)

  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(null)

  const [q, setQ] = useState("")
  const [selected, setSelected] = useState([])

  const canPick = selected.length > 0

  const close = () => onClose?.()

  const load = async (p = 1, query = q) => {
    try {
      setLoading(true)
      const res = await apiListMedia({ folder, page: p, q: query })

      // Supports:
      // { data: { data: paginator } } or { data: { data: array } }
      const payload = res?.data?.data

      const paginator = payload?.data ? payload : null
      const items = paginator?.data || payload || []

      setRows(Array.isArray(items) ? items : [])
      setPage(paginator?.current_page || p)
      setLastPage(paginator?.last_page || null)
    } catch (e) {
      toast.error("Failed to load media")
      setRows([])
      setPage(1)
      setLastPage(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return

    setTab("library")
    setSelected([])
    setQ("")
    setRows([])
    setPage(1)
    setLastPage(null)
    load(1, "")

    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev || "unset"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const isSelected = (id) => selected.some((x) => x?.id === id)

  const toggleSelect = (asset) => {
    if (!asset?.id) return

    if (!multiple) {
      setSelected([asset])
      return
    }

    setSelected((prev) => {
      const exists = prev.some((x) => x.id === asset.id)
      return exists ? prev.filter((x) => x.id !== asset.id) : [...prev, asset]
    })
  }

  const pick = () => {
    if (!selected.length) return
    if (multiple) onPick?.(selected)
    else onPick?.(selected[0])
    close()
  }

  const prevPage = () => load(Math.max(1, page - 1))
  const nextPage = () => load(page + 1)

  const nextDisabled = useMemo(() => {
    if (loading) return true
    if (lastPage == null) return false
    return page >= lastPage
  }, [loading, page, lastPage])

  const doUpload = async (file) => {
    if (!file) return

    // ✅ Avoid HEIC/HEIF issues
    const type = String(file.type || "").toLowerCase()
    if (type === "image/heic" || type === "image/heif") {
      toast.error("HEIC/HEIF not supported. Please convert to JPG/PNG.")
      return
    }

    if (!type.startsWith("image/")) {
      toast.error("Only image files are allowed")
      return
    }

    try {
      setLoading(true)

      const base64 = await fileToBase64DataUrl(file)

      // ✅ IMPORTANT: name MUST be a string, not an object
      const name = typeof file.name === "string" ? file.name : "image"

      // 🔎 Debug (keep for now, remove later)
      // console.log("image", base64?.slice(0, 40))
      // console.log("folder", folder)
      // console.log("name", name)

      const res = await apiUploadMedia({ image: base64, folder, name })

      const asset = res?.data?.data || res?.data
      toast.success("Uploaded")

      setTab("library")
      await load(1, q)

      if (asset?.id) {
        setSelected((prev) => {
          if (!multiple) return [asset]
          const exists = prev.some((x) => x.id === asset.id)
          return exists ? prev : [...prev, asset]
        })
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed")
    } finally {
      setLoading(false)
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease }}
          className="fixed inset-0 z-[90] bg-neutral-900/60 backdrop-blur-[2px] p-3 sm:p-5 grid place-items-center"
          onMouseDown={(e) => e.target === e.currentTarget && close()}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.985 }}
            transition={{ duration: 0.25, ease }}
            className="w-full max-w-6xl  bg-neutral-100 border border-white/10 shadow-2xl rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-white border-b border-black/10 px-4 sm:px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon size={18} className="text-neutral-900" />
                <div className="text-sm sm:text-base font-[800] text-neutral-900">Media</div>
                <div className="ml-2 text-xs text-neutral-500">({folder})</div>
              </div>

              <button
                onClick={close}
                className="h-10 w-10 rounded-xl bg-neutral-100 hover:bg-neutral-200 grid place-items-center transition"
                aria-label="Close"
              >
                <X size={18} className="text-neutral-700" />
              </button>
            </div>

            {/* Tabs + Search */}
            <div className="bg-white border-b border-black/10 px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTab("library")}
                  className={[
                    "h-9 px-3 rounded-xl text-sm transition",
                    tab === "library" ? "bg-neutral-900 text-white" : "bg-neutral-100 hover:bg-neutral-200 text-neutral-800",
                  ].join(" ")}
                >
                  Library
                </button>

                <button
                  onClick={() => setTab("upload")}
                  className={[
                    "h-9 px-3 rounded-xl text-sm transition inline-flex items-center gap-2",
                    tab === "upload" ? "bg-neutral-900 text-white" : "bg-neutral-100 hover:bg-neutral-200 text-neutral-800",
                  ].join(" ")}
                >
                  <UploadSimple size={16} />
                  Upload
                </button>
              </div>

              {tab === "library" && (
                <div className="flex items-center gap-2 w-full sm:w-[380px]">
                  <div className="relative w-full">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search media..."
                      className="h-10 w-full rounded-xl border border-neutral-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-neutral-900"
                    />
                  </div>
                  <button
                    onClick={() => load(1, q)}
                    className="h-10 px-4 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 transition text-sm disabled:opacity-60"
                    disabled={loading}
                  >
                    Search
                  </button>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="grid max-h-[70vh] overflow-y-auto grid-cols-1 lg:grid-cols-12">
              {/* Left */}
              <div className="lg:col-span-8 p-4 sm:p-6">
                {tab === "upload" ? (
                  <div className="rounded-2xl border border-dashed border-black/15 bg-white p-6">
                    <div className="text-sm font-[800] text-neutral-900">Upload image</div>
                    <p className="mt-1 text-sm text-neutral-600">Uploads to Bunny CDN and saves it in your media library.</p>

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="h-11 px-5 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 transition inline-flex items-center gap-2 disabled:opacity-60"
                        disabled={loading}
                      >
                        <UploadSimple size={18} />
                        Choose file
                      </button>

                      <input
                        ref={fileRef}
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={(e) => doUpload(e.target.files?.[0])}
                      />

                      <button
                        type="button"
                        onClick={() => setTab("library")}
                        className="h-11 px-5 rounded-xl bg-neutral-100 hover:bg-neutral-200 transition text-neutral-800 disabled:opacity-60"
                        disabled={loading}
                      >
                        Back
                      </button>
                    </div>

                    {loading && <div className="mt-4 text-sm text-neutral-600">Uploading…</div>}
                  </div>
                ) : (
                  <>
                    {loading && rows.length === 0 ? (
                      <div className="text-sm text-neutral-600">Loading…</div>
                    ) : rows.length === 0 ? (
                      <div className="text-sm text-neutral-600">No media found.</div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {rows.map((it) => {
                          const active = isSelected(it.id)
                          const ext = fileExt(it.url)

                          return (
                            <button
                              key={it.id}
                              type="button"
                              onClick={() => toggleSelect(it)}
                              className={[
                                "relative overflow-hidden rounded-2xl border bg-white transition text-left",
                                active ? "border-neutral-900 ring-2 ring-neutral-900/10" : "border-black/10 hover:border-black/20",
                              ].join(" ")}
                            >
                              <div className="aspect-square bg-neutral-100">
                                <img
                                  src={it.url}
                                  alt={it.name || "media"}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </div>

                              <div className="p-3">
                                <div className="text-xs font-[800] text-neutral-900 truncate">{it.name || "Untitled"}</div>
                                <div className="mt-0.5 text-[11px] text-neutral-500 truncate">
                                  {ext ? ext.toUpperCase() : it.mime || ""}
                                </div>
                              </div>

                              {active && (
                                <div className="absolute top-2 right-2 h-8 w-8 rounded-xl bg-neutral-900 text-white grid place-items-center">
                                  <Check size={16} weight="bold" />
                                </div>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between">
                      <button
                        onClick={prevPage}
                        className="h-10 px-4 rounded-xl bg-neutral-100 hover:bg-neutral-200 transition disabled:opacity-60"
                        disabled={loading || page <= 1}
                      >
                        Prev
                      </button>

                      <div className="text-xs text-neutral-500">
                        Page {page}
                        {lastPage ? ` / ${lastPage}` : ""}
                      </div>

                      <button
                        onClick={nextPage}
                        className="h-10 px-4 rounded-xl bg-neutral-100 hover:bg-neutral-200 transition disabled:opacity-60"
                        disabled={nextDisabled}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Right */}
              <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-black/10 bg-white p-4 sm:p-6">
                <div className="text-sm font-[900] text-neutral-900">Selection</div>

                {!selected.length ? (
                  <div className="mt-3 text-sm text-neutral-600">Select a file from the library.</div>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {selected.slice(0, 6).map((s) => (
                        <div key={s.id} className="overflow-hidden rounded-xl border border-black/10 bg-neutral-100">
                          <img src={s.url} alt="" className="h-20 w-full object-cover" />
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 text-xs text-neutral-500">
                      {multiple ? `${selected.length} selected` : "1 selected"}
                    </div>

                    <button
                      type="button"
                      onClick={pick}
                      className="mt-4 h-11 w-full rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 transition font-[800] disabled:opacity-60"
                      disabled={!canPick}
                    >
                      Use media
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelected([])}
                      className="mt-2 h-11 w-full rounded-xl bg-neutral-100 hover:bg-neutral-200 transition font-[700] text-neutral-800 disabled:opacity-60"
                      disabled={loading}
                    >
                      Clear selection
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
